/**
 * DepositSettings — Company deposit invoice configuration
 *
 * Saves to backend: PUT /api/invoices/deposit-settings
 * Shown in Settings → App Configuration
 */
import React, { useEffect, useState } from 'react';
import { DollarSign, Percent, Ban, Save, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input }  from '../../../components/ui/input';
import { toast }  from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DEPOSIT_TYPES = [
  { value: 'none',       label: 'No Deposit',     description: 'Invoices are created without a deposit step', icon: Ban },
  { value: 'fixed',      label: 'Fixed Amount',   description: 'Charge a flat dollar deposit amount',         icon: DollarSign },
  { value: 'percentage', label: 'Percentage',     description: 'Charge a % of the quote total as deposit',    icon: Percent },
];

async function getAuthToken() {
  const { getAuth } = await import('firebase/auth');
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

export default function DepositSettings() {
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [depositType, setDepositType]          = useState('none');
  const [depositAmount, setDepositAmount]       = useState('');
  const [depositPercentage, setDepositPct]     = useState('25');

  useEffect(() => {
    (async () => {
      try {
        const token = await getAuthToken();
        const res = await axios.get(`${API}/api/invoices/settings/deposit`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = res.data;
        setDepositType(d.depositType || 'none');
        setDepositAmount(String(d.depositAmount || ''));
        setDepositPct(String(d.depositPercentage || '25'));
      } catch (err) {
        console.error('[DepositSettings] load error', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getAuthToken();
      await axios.put(
        `${API}/api/invoices/settings/deposit`,
        {
          depositEnabled:    depositType !== 'none',
          depositType,
          depositAmount:     parseFloat(depositAmount) || 0,
          depositPercentage: parseFloat(depositPercentage) || 25,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Deposit settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-gray-400 py-4"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;
  }

  return (
    <div className="space-y-5" data-testid="deposit-settings-panel">

      {/* Type selector */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {DEPOSIT_TYPES.map((t) => {
          const Icon = t.icon;
          const active = depositType === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setDepositType(t.value)}
              data-testid={`deposit-type-${t.value}`}
              className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                active
                  ? 'border-orange-400 bg-orange-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${active ? 'text-orange-500' : 'text-gray-400'}`} />
              <div>
                <p className={`text-sm font-semibold ${active ? 'text-orange-700' : 'text-gray-800'}`}>{t.label}</p>
                <p className="text-xs text-gray-500 mt-0.5 leading-snug">{t.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Amount / percentage input */}
      {depositType === 'fixed' && (
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Deposit Amount</label>
          <div className="flex items-center gap-2 max-w-xs">
            <span className="text-gray-400 text-sm">$</span>
            <Input
              type="number"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              placeholder="100.00"
              min="0"
              step="0.01"
              data-testid="deposit-amount-input"
              className="h-10 w-36"
            />
          </div>
        </div>
      )}
      {depositType === 'percentage' && (
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Deposit Percentage</label>
          <div className="flex items-center gap-2 max-w-xs">
            <Input
              type="number"
              value={depositPercentage}
              onChange={(e) => setDepositPct(e.target.value)}
              placeholder="25"
              min="1"
              max="100"
              data-testid="deposit-percentage-input"
              className="h-10 w-24"
            />
            <span className="text-gray-500 text-sm">% of quote total</span>
          </div>
        </div>
      )}

      {/* Preview */}
      {depositType !== 'none' && (
        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2" data-testid="deposit-preview-text">
          When a job is scheduled from an approved quote, a deposit invoice will be created
          {depositType === 'fixed' && depositAmount ? ` for $${parseFloat(depositAmount).toFixed(2)}` : ''}
          {depositType === 'percentage' && depositPercentage ? ` for ${depositPercentage}% of the quote total` : ''} and a payment link sent via SMS.
        </p>
      )}

      <Button
        onClick={handleSave}
        disabled={saving}
        data-testid="save-deposit-settings-btn"
        className="text-white"
        style={{ backgroundColor: 'var(--cb-orange)' }}
      >
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save Settings</>}
      </Button>
    </div>
  );
}
